import { useCallback, useState } from 'react';

import { Tabs as BaseTabs } from '@base-ui/react';

import { getTabTypeaheadLabel, resolveTabValue } from './tabs.functions.ts';
import {
  StyledTabsHeading,
  StyledTabsList,
  StyledTabsPanel,
  StyledTabsRail,
  StyledTabsRoot,
  StyledTabsTab,
} from './tabs.styles.ts';
import type { TabsProps } from './tabs.types.ts';
import { useFirstLetterTypeahead } from './useFirstLetterTypeahead.ts';

export const Tabs = ({
  items,
  heading,
  enableFirstLetterTypeahead = false,
  orientation,
  value: controlledValue,
  defaultValue,
  onValueChange,
  activateOnFocus = true,
  loopFocus,
  listClassName,
  listStyle,
  listRender,
  ...rootProps
}: TabsProps) => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled
    ? String(controlledValue)
    : resolveTabValue(items, internalValue, defaultValue);

  const commitTabValue = useCallback(
    (nextValue: string, eventDetails?: BaseTabs.Root.ChangeEventDetails) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onValueChange?.(nextValue, eventDetails);
    },
    [isControlled, onValueChange]
  );

  const handleValueChange = useCallback<NonNullable<BaseTabs.Root.Props['onValueChange']>>(
    (nextValue, eventDetails) => {
      if (nextValue == null) {
        onValueChange?.(nextValue, eventDetails);
        return;
      }

      commitTabValue(String(nextValue), eventDetails);
    },
    [commitTabValue, onValueChange]
  );

  const typeaheadItems = items.map(({ value: itemValue, tab }) => ({
    value: String(itemValue),
    label: getTabTypeaheadLabel(tab.children),
    disabled: tab.disabled,
  }));

  const { onKeyDown: handleListKeyDown, registerTabElement } = useFirstLetterTypeahead({
    items: typeaheadItems,
    value,
    onValueChange: commitTabValue,
    enabled: enableFirstLetterTypeahead,
  });

  const resolvedOrientation = orientation ?? 'horizontal';

  return (
    <StyledTabsRoot
      {...rootProps}
      value={value}
      onValueChange={handleValueChange}
      orientation={orientation}
      $orientation={resolvedOrientation}
    >
      <StyledTabsRail $orientation={resolvedOrientation}>
        {heading !== undefined && (
          <StyledTabsHeading type='heading_small'>{heading}</StyledTabsHeading>
        )}
        <StyledTabsList
          activateOnFocus={activateOnFocus}
          loopFocus={loopFocus}
          onKeyDown={handleListKeyDown}
          className={listClassName}
          style={listStyle}
          render={listRender}
          $orientation={resolvedOrientation}
        >
          {items.map(({ value: itemValue, tab }) => {
            const { children: tabChildren, ...tabProps } = tab;

            return (
              <StyledTabsTab
                key={String(itemValue)}
                {...tabProps}
                ref={
                  enableFirstLetterTypeahead
                    ? element => registerTabElement(String(itemValue), element)
                    : undefined
                }
                value={itemValue}
                $orientation={resolvedOrientation}
              >
                {tabChildren}
              </StyledTabsTab>
            );
          })}
        </StyledTabsList>
      </StyledTabsRail>
      {items.map(({ value: itemValue, panel }) => {
        const { children: panelChildren, ...panelProps } = panel;

        return (
          <StyledTabsPanel
            key={String(itemValue)}
            {...panelProps}
            value={itemValue}
            $orientation={resolvedOrientation}
          >
            {panelChildren}
          </StyledTabsPanel>
        );
      })}
    </StyledTabsRoot>
  );
};
